import { useAuth } from '../hooks/use-auth';
import { usePermissions } from '../hooks/use-permissions';
import { Loader2 } from "lucide-react";
import { Redirect, Route } from "wouter";

export function ProtectedRoute({
  path,
  component: Component,
  requiredFeature,
}: {
  path: string;
  component: () => React.JSX.Element;
  /** If set, user must have access to this feature (role permission) or is redirected to dashboard */
  requiredFeature?: string;
}) {
  const { user, isLoading, isLoggingOut } = useAuth();
  const { canAccessFeature, isLoading: permissionsLoading } = usePermissions();

  // Show loading state while checking authentication or permissions (for role-guarded routes)
  if (isLoading || isLoggingOut || (requiredFeature && permissionsLoading)) {
    return (
      <Route path={path}>
        <div className="flex items-center justify-center min-h-screen bg-slate-900">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-amber-500 mx-auto" />
            <p className="mt-4 text-slate-300">Loading...</p>
          </div>
        </div>
      </Route>
    );
  }

  // Check localStorage as fallback before redirecting
  // This prevents redirects when user query temporarily fails
  const hasLocalStorageUser = () => {
    try {
      const localUser = localStorage.getItem('user');
      if (localUser) {
        const parsed = JSON.parse(localUser);
        return parsed && parsed.id && parsed.email;
      }
    } catch (e) {
      return false;
    }
    return false;
  };

  // Only redirect if we're sure the user is not authenticated
  if ((!user || !user.id || !user.email) && !hasLocalStorageUser()) {
    return (
      <Route path={path}>
        <Redirect to="/simple-login" />
      </Route>
    );
  }

  // Role-based: if this route requires a feature, block direct URL access when user lacks permission
  if (requiredFeature && !permissionsLoading && !canAccessFeature(requiredFeature)) {
    return (
      <Route path={path}>
        <Redirect to="/dashboard" />
      </Route>
    );
  }

  return <Route path={path} component={Component} />;
}
